import { useState } from 'react';
import { format } from 'date-fns';
import {
  CheckCircle2,
  FilePlus2,
  Flag,
  History,
  MessageSquareWarning,
  PenLine,
  RotateCcw,
  Send,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AdminComment,
  AdminCommentsList,
} from '@/components/AdminComments/AdminCommentsPanel';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useGetPostActivityQuery } from '@/redux/postActivity/postActivity.api.slice';
import {
  HistoryTarget,
  PostActivityEntry,
  PostActivityType,
} from '@/redux/postActivity/postActivity.types';
import FieldChanges from './FieldChanges';
import FileChanges from './FileChanges';

const EVENT: Record<
  PostActivityType,
  { title: string; icon: LucideIcon; tone: string }
> = {
  baseline: {
    title: 'History started',
    icon: Flag,
    tone: 'bg-neutral-100 text-neutral-600',
  },
  created: {
    title: 'Created',
    icon: FilePlus2,
    tone: 'bg-neutral-100 text-neutral-600',
  },
  submitted: {
    title: 'Submitted for review',
    icon: Send,
    tone: 'bg-blue-100 text-blue-600',
  },
  resubmitted: {
    title: 'Updated and resubmitted',
    icon: PenLine,
    tone: 'bg-blue-100 text-blue-600',
  },
  approved: {
    title: 'Approved and published',
    icon: CheckCircle2,
    tone: 'bg-green-100 text-green-700',
  },
  rejected: {
    title: 'Changes requested',
    icon: MessageSquareWarning,
    tone: 'bg-amber-100 text-amber-700',
  },
  suspended: {
    title: 'Suspended',
    icon: ShieldAlert,
    tone: 'bg-red-100 text-red-700',
  },
  comment_resolved: {
    title: 'Comment resolved',
    icon: CheckCircle2,
    tone: 'bg-green-100 text-green-700',
  },
  comment_reopened: {
    title: 'Comment reopened',
    icon: RotateCcw,
    tone: 'bg-amber-100 text-amber-700',
  },
};

const actorName = (entry: PostActivityEntry) => {
  if (entry.actor?.username) return `@${entry.actor.username}`;
  return entry.actorKind === 'admin' ? 'Admin' : 'Maker';
};

const countOf = (list?: unknown[]) => list?.length ?? 0;

const counted = (count: number, word: string) =>
  count === 1 ? `1 ${word}` : `${count} ${word}s`;

const changeSummary = (entry: PostActivityEntry) => {
  const parts: [number, string][] = [
    [countOf(entry.changes), 'field'],
    [countOf(entry.fileChanges), 'file'],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([count, word]) => counted(count, word))
    .join(', ');
};

function Comments({ entry }: { entry: PostActivityEntry }) {
  if (!entry.comments?.length) return null;
  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {entry.comments.map((comment) => (
        <li
          key={comment.id}
          className="whitespace-pre-wrap rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-950"
        >
          {comment.text}
        </li>
      ))}
    </ul>
  );
}

function Changes({ entry }: { entry: PostActivityEntry }) {
  return (
    <div className="mt-3 flex flex-col gap-4">
      <FieldChanges changes={entry.changes ?? []} labels={entry.labels} />
      <FileChanges changes={entry.fileChanges ?? []} labels={entry.labels} />
    </div>
  );
}

function Diff({ entry, open }: { entry: PostActivityEntry; open: boolean }) {
  const [isOpen, setIsOpen] = useState(open);
  const summary = changeSummary(entry);
  if (!summary) return null;
  const label = isOpen ? 'Hide changes' : `Show changes (${summary})`;
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="text-sm font-medium text-green-800 underline underline-offset-4 hover:text-green-950"
      >
        {label}
      </button>
      {isOpen && <Changes entry={entry} />}
    </div>
  );
}

function Entry({
  entry,
  isLatestUpdate,
}: {
  entry: PostActivityEntry;
  isLatestUpdate: boolean;
}) {
  const event = EVENT[entry.type];
  const Icon = event.icon;
  return (
    <li
      className="relative flex gap-3 pb-6 last:pb-0"
      data-testid={`history-${entry.type}`}
    >
      <span
        className={cn(
          'z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          event.tone
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 pt-1">
        <p className="text-sm">
          <span className="font-semibold">{event.title}</span>{' '}
          <span className="text-muted-foreground">by {actorName(entry)}</span>
        </p>
        <p className="text-xs text-muted-foreground">
          {format(new Date(entry.createdAt), 'MMM d, yyyy · h:mm a')}
        </p>
        <Comments entry={entry} />
        <Diff entry={entry} open={isLatestUpdate} />
      </div>
    </li>
  );
}

function Timeline({ entries }: { entries: PostActivityEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No history yet. It is recorded for submissions from this release onward.
      </p>
    );
  }
  const latestUpdate = [...entries]
    .reverse()
    .find((entry) => entry.type === 'resubmitted');
  return (
    <ol className="relative before:absolute before:left-4 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
      {entries.map((entry) => (
        <Entry
          key={entry._id}
          entry={entry}
          isLatestUpdate={entry === latestUpdate}
        />
      ))}
    </ol>
  );
}

const countLabel = (count: number) => (count > 0 ? ` (${count})` : '');

function HistoryTab({
  entries,
  isLoading,
}: {
  entries?: PostActivityEntry[];
  isLoading: boolean;
}) {
  if (isLoading) return <Skeleton className="h-40 w-full" />;
  return <Timeline entries={entries ?? []} />;
}

function Feedback({
  comments,
  token,
  onRefetch,
}: {
  comments: AdminComment[];
  token: string;
  onRefetch: () => void;
}) {
  if (comments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No admin feedback yet.</p>
    );
  }
  return (
    <AdminCommentsList
      comments={comments}
      token={token}
      onRefetch={onRefetch}
    />
  );
}

/** Admin feedback, plus every submission and review with what each resubmission changed. Opens itself when there is feedback. */
export default function ReviewHistorySheet({
  target,
  token,
  comments,
  onRefetch,
}: {
  target: HistoryTarget;
  token: string;
  comments: AdminComment[];
  onRefetch: () => void;
}) {
  const { data, isLoading, refetch } = useGetPostActivityQuery({
    target,
    token,
  });
  const refetchBoth = () => {
    onRefetch();
    refetch();
  };
  const open = countLabel(
    comments.filter((comment) => !comment.isResolved).length
  );
  const hasFeedback = comments.length > 0;
  return (
    <Sheet defaultOpen={hasFeedback}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-yellow-500 bg-yellow-400 text-yellow-950 hover:bg-yellow-500"
        >
          <History className="h-4 w-4" />
          Review history &amp; feedback{open}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        <SheetHeader className="border-b p-5 pr-12 text-left">
          <SheetTitle className="font-display text-xl font-semibold">
            Review history &amp; feedback
          </SheetTitle>
          <SheetDescription>
            Feedback from the Onefinity team, and every submission and review
            with what changed on each resubmission.
          </SheetDescription>
        </SheetHeader>
        <Tabs
          defaultValue={hasFeedback ? 'feedback' : 'history'}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList className="mx-5 mt-4 self-start">
            <TabsTrigger value="feedback">Feedback{open}</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="feedback" className="flex-1 overflow-y-auto p-5">
            <Feedback
              comments={comments}
              token={token}
              onRefetch={refetchBoth}
            />
          </TabsContent>
          <TabsContent value="history" className="flex-1 overflow-y-auto p-5">
            <HistoryTab entries={data} isLoading={isLoading} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
